import { useEffect, useState } from "react";
import { Building2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useApi, errMsg } from "@/lib/api";
import { useSession } from "@/context/Session";
import { t } from "@/i18n";
import { RepairSpend, RepairTotal, useRepairSpend } from "@/components/RepairSpend";

const FIELDS = [["address", "Address"], ["type", "Type"], ["tenant", "Tenant"], ["lease", "Lease"], ["notes", "Notes"]];

function PropertyCard({ p, admin, onSave, spend }) {
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState(p);
  const save = async () => { if (await onSave(f)) setEdit(false); };
  return (
    <div className="card stack-sm" data-testid={`property-card-${p.id}`}>
      <div className="flex items-center gap-2">
        <Building2 size={18} className="text-[var(--accent-primary)]" />
        {edit ? <input className="field !py-1.5 font-semibold" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="property-name-input" />
          : <p className="text-lg font-semibold" data-testid="property-name">{p.name}</p>}
        {admin && !edit && <button className="ml-auto btn btn-ghost btn-sm" onClick={() => setEdit(true)} data-testid={`property-edit-${p.id}`}><Pencil size={13} /> {t("Edit")}</button>}
      </div>
      {FIELDS.map(([k, label]) => (
        <div key={k} className="text-sm" data-testid={`property-${k}`}>
          <span className="mono-label">{t(label)}</span>
          {edit ? <textarea className="field text-sm mt-1" rows={k === "notes" || k === "lease" ? 3 : 1} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`property-${k}-input`} />
            : <p className="mt-0.5">{p[k] || "—"}</p>}
        </div>
      ))}
      {spend && <RepairSpend row={spend.properties.find((x) => x.property_id === p.id)} year={spend.year} />}
      {p.sops.length > 0 && <div className="flex flex-wrap gap-1.5" data-testid="property-sops">{p.sops.map((s) => <Link key={s.id} to={`/sops/${s.id}`} className="chip chip-muted hover:text-[var(--accent-primary)]">{s.title}</Link>)}</div>}
      {edit && <div className="flex gap-2"><button className="btn btn-primary btn-sm" onClick={save} data-testid="property-save">{t("Save")}</button><button className="btn btn-ghost btn-sm" onClick={() => { setF(p); setEdit(false); }}>{t("Cancel")}</button></div>}
    </div>
  );
}

export default function Properties() {
  const api = useApi();
  const { me } = useSession();
  const [items, setItems] = useState(null);
  const spend = useRepairSpend(me.role === "admin");
  const load = () => api.get("/properties").then((r) => setItems(r.data)).catch((e) => { toast.error(errMsg(e)); setItems([]); });
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async (p) => {
    try { await api.put(`/properties/${p.id}`, p); toast.success(t("Saved")); load(); return true; } catch (e) { toast.error(errMsg(e)); return false; }
  };
  return (
    <div className="page stack" data-testid="properties-page">
      <h1 className="text-3xl md:text-4xl font-bold tracking-tight rise">{t("Properties")}</h1>
      <RepairTotal data={spend} />
      {items === null ? <p className="faint">{t("Loading…")}</p> : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 rise d1">
          {items.map((p) => <PropertyCard key={p.id} p={p} admin={me.role === "admin"} onSave={save} spend={spend} />)}
        </div>
      )}
    </div>
  );
}
